import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getAttendanceChart, getRecentIncidents, getAlertas, getDashboardIndicadores, getCalendarioResumen } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Student, CalendarCheck, Warning, Bell, Notebook, Users,
  ArrowUp, ArrowDown, ArrowRight, Clock, ChartBar, TrendUp, Calendar
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [indicadores, setIndicadores] = useState(null);
  const [calendarioResumen, setCalendarioResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, chartRes, alertsRes] = await Promise.all([
          getDashboardStats(),
          getAttendanceChart(7),
          getAlertas({ atendida: false })
        ]);
        
        setStats(statsRes.data);
        setChartData(chartRes.data);
        setAlerts(alertsRes.data.slice(0, 5));
        
        if (user && ['administrador', 'direccion', 'prefectura'].includes(user.rol)) {
          const [incidentsRes, indicadoresRes, calendarioRes] = await Promise.all([
            getRecentIncidents(5),
            getDashboardIndicadores(),
            getCalendarioResumen()
          ]);
          setIncidents(incidentsRes.data);
          setIndicadores(indicadoresRes.data);
          setCalendarioResumen(calendarioRes.data);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching dashboard data:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchData();
    }
  }, [user]);

  const StatCard = ({ title, value, icon: Icon, trend, link, color = "primary" }) => (
    <Card className="card-hover" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-heading font-bold mt-1">{value ?? '-'}</p>
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                <span>{Math.abs(trend)}% vs semana anterior</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color === 'primary' ? 'bg-primary/10 text-primary' : `bg-${color}-100 text-${color}-600`}`}>
            <Icon size={24} weight="duotone" />
          </div>
        </div>
        {link && (
          <Link to={link} className="flex items-center gap-1 text-sm text-primary hover:underline mt-4">
            Ver detalles <ArrowRight size={14} />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  const getRolGreeting = () => {
    const greetings = {
      administrador: 'Panel de Control Total',
      direccion: 'Vista General del Plantel',
      prefectura: 'Panel de Operaciones',
      docente: 'Panel Académico'
    };
    return greetings[user?.rol] || 'Dashboard';
  };

  const getGravedadColor = (gravedad) => {
    const colors = {
      leve: 'bg-blue-100 text-blue-800',
      media: 'bg-amber-100 text-amber-800',
      grave: 'bg-red-100 text-red-800'
    };
    return colors[gravedad] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">
          Bienvenido, {user?.nombre_completo?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground">{getRolGreeting()}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Alumnos" 
          value={stats.total_alumnos} 
          icon={Student}
          link="/alumnos"
        />
        
        {hasPermission(['administrador', 'direccion', 'prefectura']) && (
          <StatCard 
            title="Asistencia Hoy" 
            value={stats.asistencia_hoy} 
            icon={CalendarCheck}
            link="/asistencia"
          />
        )}
        
        <StatCard 
          title="Alumnos PATRE" 
          value={stats.alumnos_patre} 
          icon={Warning}
          link="/patre"
          color="amber"
        />
        
        <StatCard 
          title="Alertas Pendientes" 
          value={stats.alertas_pendientes} 
          icon={Bell}
          link="/alertas"
          color="red"
        />
        
        {hasPermission(['administrador']) && (
          <>
            <StatCard 
              title="Total Usuarios" 
              value={stats.total_usuarios} 
              icon={Users}
              link="/usuarios"
            />
            <StatCard 
              title="Usuarios Activos" 
              value={stats.usuarios_activos} 
              icon={Users}
            />
          </>
        )}
        
        {hasPermission(['administrador', 'direccion', 'prefectura']) && (
          <>
            <StatCard 
              title="Bitácora Pendiente" 
              value={stats.bitacora_pendiente} 
              icon={Notebook}
              link="/bitacora"
            />
            <StatCard 
              title="Incidencias del Mes" 
              value={stats.incidencias_mes} 
              icon={Warning}
              link="/expediente"
            />
          </>
        )}
      </div>

      {/* Charts and Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Chart */}
        {hasPermission(['administrador', 'direccion', 'prefectura']) && chartData.length > 0 && (
          <Card data-testid="attendance-chart">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Asistencia Semanal</CardTitle>
              <CardDescription>Últimos 7 días</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="fecha" 
                      tickFormatter={(value) => {
                        // value es 'YYYY-MM-DD'. new Date(string) lo interpreta como UTC,
                        // por lo que en zonas horarias negativas se muestra el día anterior.
                        // Construimos la fecha como local para evitar el desfase.
                        const [y, m, d] = (value || '').split('-').map(Number);
                        if (!y || !m || !d) return value;
                        const date = new Date(y, m - 1, d);
                        return date.toLocaleDateString('es-MX', { weekday: 'short' });
                      }}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="asistencia" name="Asistencia" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="retardo" name="Retardo" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="falta" name="Falta" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Alerts */}
        <Card data-testid="recent-alerts">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Alertas Recientes</CardTitle>
              <CardDescription>Pendientes de atención</CardDescription>
            </div>
            <Link to="/alertas">
              <Button variant="ghost" size="sm">
                Ver todas <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Bell size={40} className="mb-2 opacity-50" />
                  <p className="text-sm">No hay alertas pendientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert, index) => (
                    <div 
                      key={alert.id} 
                      className="p-3 rounded-lg border border-border hover:border-primary/20 transition-colors stagger-item"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{alert.mensaje}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {alert.tipo.replace(/_/g, ' ')}
                            </Badge>
                            {alert.matricula && (
                              <span className="text-xs text-muted-foreground">
                                Mat: {alert.matricula}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center text-xs text-muted-foreground">
                          <Clock size={12} className="mr-1" />
                          {new Date(alert.fecha_creacion).toLocaleDateString('es-MX')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Incidents */}
        {hasPermission(['administrador', 'direccion', 'prefectura']) && (
          <Card data-testid="recent-incidents">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">Incidencias Recientes</CardTitle>
                <CardDescription>Últimos registros en expediente</CardDescription>
              </div>
              <Link to="/expediente">
                <Button variant="ghost" size="sm">
                  Ver todas <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[280px]">
                {incidents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Warning size={40} className="mb-2 opacity-50" />
                    <p className="text-sm">No hay incidencias recientes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {incidents.map((incident, index) => (
                      <div 
                        key={incident.id} 
                        className="p-3 rounded-lg border border-border hover:border-primary/20 transition-colors stagger-item"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{incident.descripcion}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge className={`text-xs ${getGravedadColor(incident.gravedad)}`}>
                                {incident.gravedad}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {incident.grado}° {incident.grupo}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Mat: {incident.matricula}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Indicadores Avanzados - Solo para Admin/Dirección/Prefectura */}
      {hasPermission(['administrador', 'direccion', 'prefectura']) && indicadores && (
        <div className="mt-8">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <ChartBar size={24} />
            Indicadores del Mes ({indicadores.periodo?.mes})
          </h2>
          
          {/* Resumen de suspensiones por retardos */}
          {indicadores.suspensiones_por_retardos > 0 && (
            <Card className="mb-4 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Warning size={24} className="text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">
                      {indicadores.suspensiones_por_retardos} suspensiones por acumulación de retardos este mes
                    </p>
                    <p className="text-sm text-amber-600">
                      Regla aplicada: 5 retardos = 1 día de suspensión
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="faltas" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="faltas">Top Faltas</TabsTrigger>
              <TabsTrigger value="retardos">Top Retardos</TabsTrigger>
              <TabsTrigger value="incidencias">Top Incidencias</TabsTrigger>
              <TabsTrigger value="turnos">Por Turno</TabsTrigger>
            </TabsList>

            {/* Top Faltas */}
            <TabsContent value="faltas" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Alumnos con más faltas del mes</CardTitle>
                </CardHeader>
                <CardContent>
                  {indicadores.top_faltas?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No hay datos de faltas este mes</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Alumno</TableHead>
                          <TableHead>Matrícula</TableHead>
                          <TableHead>Grado</TableHead>
                          <TableHead>Turno</TableHead>
                          <TableHead className="text-right">Faltas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {indicadores.top_faltas?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.nombre || 'N/A'}</TableCell>
                            <TableCell className="font-mono text-sm">{item.matricula}</TableCell>
                            <TableCell>{item.grado}° {item.grupo}</TableCell>
                            <TableCell className="capitalize">{item.turno}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="destructive">{item.faltas}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Top Retardos */}
            <TabsContent value="retardos" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Alumnos con más retardos del mes</CardTitle>
                  <CardDescription>5 retardos generan suspensión automática de 1 día</CardDescription>
                </CardHeader>
                <CardContent>
                  {indicadores.top_retardos?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No hay datos de retardos este mes</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Alumno</TableHead>
                          <TableHead>Matrícula</TableHead>
                          <TableHead>Grado</TableHead>
                          <TableHead>Turno</TableHead>
                          <TableHead className="text-right">Retardos</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {indicadores.top_retardos?.map((item, index) => (
                          <TableRow key={index} className={item.retardos >= 5 ? 'bg-amber-50' : ''}>
                            <TableCell className="font-medium">{item.nombre || 'N/A'}</TableCell>
                            <TableCell className="font-mono text-sm">{item.matricula}</TableCell>
                            <TableCell>{item.grado}° {item.grupo}</TableCell>
                            <TableCell className="capitalize">{item.turno}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={item.retardos >= 5 ? 'destructive' : 'secondary'}>
                                {item.retardos} {item.retardos >= 5 && '⚠️'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Top Incidencias */}
            <TabsContent value="incidencias" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Alumnos con más incidencias registradas</CardTitle>
                </CardHeader>
                <CardContent>
                  {indicadores.top_incidencias?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No hay incidencias registradas</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Alumno</TableHead>
                          <TableHead>Matrícula</TableHead>
                          <TableHead>Grado</TableHead>
                          <TableHead>Turno</TableHead>
                          <TableHead className="text-right">Incidencias</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {indicadores.top_incidencias?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.nombre || 'N/A'}</TableCell>
                            <TableCell className="font-mono text-sm">{item.matricula}</TableCell>
                            <TableCell>{item.grado}° {item.grupo}</TableCell>
                            <TableCell className="capitalize">{item.turno}</TableCell>
                            <TableCell className="text-right">
                              <Badge>{item.incidencias}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Comparativo por Turno */}
            <TabsContent value="turnos" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Comparativo de asistencia por turno</CardTitle>
                </CardHeader>
                <CardContent>
                  {indicadores.comparativo_turno?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No hay datos de asistencia este mes</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {indicadores.comparativo_turno?.map((turno, index) => {
                        const porcentajeAsistencia = turno.total > 0 
                          ? Math.round((turno.asistencias / turno.total) * 100) 
                          : 0;
                        return (
                          <Card key={index} className="border-2">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-lg capitalize flex items-center gap-2">
                                {turno._id === 'matutino' ? '🌅' : '🌆'} Turno {turno._id}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="text-center p-3 bg-green-50 rounded-lg">
                                  <p className="text-2xl font-bold text-green-600">{turno.asistencias}</p>
                                  <p className="text-xs text-muted-foreground">Asistencias</p>
                                </div>
                                <div className="text-center p-3 bg-red-50 rounded-lg">
                                  <p className="text-2xl font-bold text-red-600">{turno.faltas}</p>
                                  <p className="text-xs text-muted-foreground">Faltas</p>
                                </div>
                                <div className="text-center p-3 bg-amber-50 rounded-lg">
                                  <p className="text-2xl font-bold text-amber-600">{turno.retardos}</p>
                                  <p className="text-xs text-muted-foreground">Retardos</p>
                                </div>
                                <div className="text-center p-3 bg-blue-50 rounded-lg">
                                  <p className="text-2xl font-bold text-blue-600">{porcentajeAsistencia}%</p>
                                  <p className="text-xs text-muted-foreground">% Asistencia</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Próximos días no laborables */}
          {calendarioResumen && calendarioResumen.proximos_dias_no_laborables?.length > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar size={18} />
                  Próximos días no laborables
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {calendarioResumen.proximos_dias_no_laborables.map((dia) => (
                    <Badge key={`${dia.fecha}-${dia.tipo}`} variant="outline" className="py-2 px-3">
                      <span className="font-medium">{dia.fecha}</span>
                      <span className="text-muted-foreground ml-2">
                        ({dia.tipo}{dia.descripcion ? `: ${dia.descripcion}` : ''})
                      </span>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
